import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications } from '../../hooks/useNotifications';
import { NOTIFICATION_TYPE_LABELS } from '../../constants/notificationConstants';
import NotificationCard from './NotificationCard';
import LoadingSpinner from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';

const NotificationInbox = () => {
  const { isMobile, getResponsiveValue } = useTheme();
  const { notifications, loading, error, markAsRead, loadNotifications } = useNotifications();
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [localNotifications, setLocalNotifications] = useState([]);

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  // Sync local notifications with the hook notifications
  useEffect(() => {
    setLocalNotifications(notifications);
  }, [notifications]);

  // Filter notifications based on selected filter
  const filteredNotifications = localNotifications.filter(notification => {
    switch (filter) {
      case 'unread':
        return !notification.isRead;
      case 'read':
        return notification.isRead;
      default:
        return true;
    }
  });

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      try {
        await markAsRead(notification.id);
        
        // Update local state immediately
        setLocalNotifications(prev => 
          prev.map(n => 
            n.id === notification.id 
              ? { ...n, isRead: true }
              : n
          )
        );
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  // Handle response updates from notification cards
  const handleNotificationUpdate = (notificationId, response) => {
    console.log('NotificationInbox: Updating notification', notificationId, response);
    
    setLocalNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { 
              ...notification, 
              userResponse: { 
                response, 
                respondedAt: new Date() 
              } 
            }
          : notification
      )
    );
  };

  const filterOptions = [
    { value: 'all', label: 'All Notifications' },
    { value: 'unread', label: 'Unread' },
    { value: 'read', label: 'Read' }
  ];

  const getUnreadCount = () => {
    return localNotifications.filter(n => !n.isRead).length;
  };

  // Manual refresh function
  const handleRefresh = async () => {
    try {
      await loadNotifications();
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    }
  };

  if (loading && localNotifications.length === 0) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading notifications..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-secondary">
      <div className={`max-w-4xl mx-auto ${containerPadding}`}>
        
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className={`${titleSize} font-bold text-text-primary`}>
              Notifications
            </h1>
            <div className="flex items-center space-x-3">
              {getUnreadCount() > 0 && (
                <div className="bg-notification-badge text-notification-badge-text px-3 py-1 rounded-full text-sm font-medium">
                  {getUnreadCount()} unread
                </div>
              )}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="p-2 text-text-tertiary hover:text-text-primary hover:bg-surface-hover rounded-lg transition-colors"
                title="Refresh notifications"
              >
                <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex space-x-1 bg-surface-secondary rounded-lg p-1">
            {filterOptions.map(option => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  filter === option.value
                    ? 'bg-surface-elevated text-text-primary shadow-theme-sm'
                    : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
                }`}
              >
                {option.label}
                {option.value === 'unread' && getUnreadCount() > 0 && (
                  <span className="ml-2 bg-notification-badge text-notification-badge-text px-2 py-0.5 rounded-full text-xs">
                    {getUnreadCount()}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-error-subtle text-error rounded-xl border border-border-error">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Error loading notifications</div>
                <div className="text-sm mt-1">{error}</div>
              </div>
              <button
                onClick={handleRefresh}
                className="px-3 py-1 bg-error text-white rounded text-sm hover:bg-error-hover"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <div className="bg-surface-elevated rounded-xl border border-border-primary p-8">
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 17h5l-5 5V12a1 1 0 00-1-1h-1m-1 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h6m2 0V4a1 1 0 011-1h1a1 1 0 011 1v1m0 0h2" />
                </svg>
              }
              title={
                filter === 'unread' ? "No unread notifications" :
                filter === 'read' ? "No read notifications" :
                "No notifications yet"
              }
              description={
                filter === 'unread' ? "All notifications have been read" :
                filter === 'read' ? "No notifications have been read yet" :
                "You'll see notifications from administrators here when they send them"
              }
            />
          </div>
        ) : (
          <div className="space-y-4">
            {filteredNotifications.map(notification => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                onClick={() => handleNotificationClick(notification)}
                onResponseUpdate={handleNotificationUpdate}
              />
            ))}
          </div>
        )}

        {/* Statistics */}
        {localNotifications.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-surface-elevated rounded-xl p-4 border border-border-primary">
              <div className="flex items-center">
                <div className="p-2 bg-accent-subtle rounded-lg">
                  <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5V12a1 1 0 00-1-1h-1m-1 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h6m2 0V4a1 1 0 011-1h1a1 1 0 011 1v1m0 0h2" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-text-secondary">Total</p>
                  <p className="text-xl font-semibold text-text-primary">{localNotifications.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-surface-elevated rounded-xl p-4 border border-border-primary">
              <div className="flex items-center">
                <div className="p-2 bg-warning-subtle rounded-lg">
                  <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-text-secondary">Unread</p>
                  <p className="text-xl font-semibold text-text-primary">{getUnreadCount()}</p>
                </div>
              </div>
            </div>

            <div className="bg-surface-elevated rounded-xl p-4 border border-border-primary">
              <div className="flex items-center">
                <div className="p-2 bg-success-subtle rounded-lg">
                  <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-text-secondary">Read</p>
                  <p className="text-xl font-semibold text-text-primary">
                    {localNotifications.length - getUnreadCount()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationInbox;